import { motion } from "framer-motion";
import { CheckCircle, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface SuccessMessageProps {
  scheduledDate?: Date | null;
  scheduledTime?: string | null;
  onReset?: () => void;
}

const SuccessMessage = ({
  scheduledDate,
  scheduledTime,
  onReset,
}: SuccessMessageProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center text-center space-y-8 py-8"
    >
      {/* Success icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="relative"
      >
        <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl scale-150" />
        <div className="relative bg-primary/10 rounded-full p-6">
          <CheckCircle className="h-16 w-16 text-primary" strokeWidth={1.5} />
        </div>
      </motion.div>

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-3"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
          Você está a um passo de crescer!
        </h2>
        <p className="text-muted-foreground text-base max-w-sm">
          Recebemos seus dados e nossa equipe entrará em contato em breve.
        </p>
      </motion.div>

      {/* Scheduled date */}
      {scheduledDate && scheduledTime && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-accent/50 rounded-2xl p-6 w-full max-w-sm"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="bg-primary/10 rounded-xl p-3">
              <Calendar className="h-6 w-6 text-primary" />
            </div>
            <div className="text-left">
              <p className="text-sm text-muted-foreground">Sua reunião</p>
              <p className="font-semibold text-foreground">
                {format(scheduledDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
              </p>
              <p className="text-primary font-medium">às {scheduledTime}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="w-full space-y-4"
      >
        <a href="/" className="block">
          <Button
            variant="outline"
            className="w-full h-14 rounded-2xl border-border/50 hover:bg-accent text-base font-medium group"
          >
            Ver como funciona
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </a>

        {onReset && (
          <button
            onClick={onReset}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Fazer novo cadastro
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};

export default SuccessMessage;
